<template>
  <div>
    <!-- Loading -->
    <vue-element-loading
      :active="isLoading"
      spinner="spinner"
      color="#FF6700"
      :is-full-screen="true"
      :background-color="'rgba(255, 255, 255, .3)'"
    />

    <BaseBreadCrumb :items="breadcrumbs" />

    <!-- Learning Unit Name -->
    <div ref="learning-unit-name" class="mt-u4 h5 display-4 font-weight-bold">
      {{ learningUnit ? learningUnit.name : ' ' }}
    </div>

    <!-- Page name -->
    <div ref="action-title" class="mt-u9 h5 font-weight-bold">Edit Video Conference</div>

    <!-- Title -->
    <div ref="input-title" class="mt-u8">
      <label for="input-title" class="mb-u4"> Title </label>
      <b-form-input
        id="input-title"
        v-model="$v.form.title.$model"
        type="text"
        placeholder="Insert your title"
        :disabled="isSubmitting"
        :state="$v.form.title.$anyError ? false : null"
        aria-describedby="input-title-invalid-feedback"
      />
      <b-form-invalid-feedback id="input-title-invalid-feedback" class="mt-u2">
        {{ titleErrorMessage }}
      </b-form-invalid-feedback>
    </div>

    <!-- Description -->
    <div class="mt-u8">
      <label for="input-description" class="mb-u4">
        Description
        <span class="text-muted">(Optional)</span>
      </label>
      <quill-editor
        id="input-description"
        v-model="form.description"
        :options="quillOptionsDescription"
        class="input__description"
      />
    </div>

    <!-- Status -->
    <div class="mt-u8">
      <label for="input-status" class="mb-u4"> Status </label>
      <b-alert show variant="info"> Bangkok Time </b-alert>
      <div class="mt-u6">
        <b-form-radio v-model="form.is_published" :value="false">Unpublished</b-form-radio>
        <b-form-radio v-model="form.is_published" :value="true" class="mt-u4">
          Published
        </b-form-radio>
        <BaseDatePicker
          :existed-data="form.published_at"
          class="ml-u8 mt-u3"
          date-picker-class="w-25 mr-2"
          @emitDate="
            (date) => {
              form.published_at = date;
            }
          "
        />
      </div>
    </div>

    <!-- Display -->
    <div v-if="records && records.length > 0" class="mt-u8">
      <label for="input-display"> Display </label>
      <div v-for="(record, index) in records" :key="record.id" class="mt-u4">
        <b-form-checkbox
          v-if="record.vimeo.upload_status === 'complete'"
          v-model="record.is_listed"
        >
          <div>Recording {{ index + 1 }}</div>
          <div class="mt-u1 text-description">
            Duration : {{ formatRecordDuration(record.vimeo.duration) }}
          </div>
          <div class="mt-u2 display-cover" @click.prevent="showModalVideo(index)">
            <b-img :src="require(`@/assets/default-cover-video.jpg`)" fluid />
          </div>
        </b-form-checkbox>

        <!-- Modal -->
        <ViewVideoAndChatModal :post="post" :index="index" :meeting-record="record" />
      </div>
    </div>

    <!-- Button Save -->
    <div class="border-top pt-u5 mt-u9">
      <b-button
        id="button-save"
        type="submit"
        class="mr-u6"
        variant="success"
        :disabled="isSubmitting"
        @click="submit"
      >
        <span v-if="!isSubmitting">Save</span>
        <span v-else class="pr-2 pl-2">
          <b-spinner label="Loading..." variant="secondary" small />
        </span>
      </b-button>
      <b-button
        id="button-back-to-previous"
        type="reset"
        variant="link btn-link-dark h-50"
        :disabled="isSubmitting"
        @click="goBackToPreviousPage"
      >
        Back to previous page
      </b-button>
    </div>

    <!-- Space -->
    <div class="mt-u9"></div>
  </div>
</template>

<script>
import moment from 'moment';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import BaseBreadCrumb from '@/components/base-components/BaseBreadCrumb.vue';
import BaseDatePicker from '@/components/base-components/BaseDatePicker.vue';
import ViewVideoAndChatModal from '@/views/VideoConference/components/ViewVideoAndChatModal.vue';
import { notificationMixin } from '@/mixins/notificationMixin';
import { Post, PostSchedule, MeetingRecord } from '@/models';

export default {
  components: {
    BaseBreadCrumb,
    BaseDatePicker,
    ViewVideoAndChatModal
  },

  mixins: [notificationMixin],

  validations: {
    form: {
      title: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(255)
      }
    }
  },

  data: () => ({
    isLoading: false,
    isError: false,
    isSubmitting: false,
    form: {
      title: '',
      description: '',
      is_published: false,
      published_at: ''
    },
    records: [],
    quillOptionsDescription: {
      modules: {
        toolbar: [[{ list: 'ordered' }, { list: 'bullet' }], ['link']]
      },
      placeholder: 'Insert your description'
    }
  }),

  // TODO: learningUnitId and sectionId should get from postId, not from params
  computed: {
    learningUnitId() {
      return Number.parseInt(this.$route.params.learningUnitId);
    },

    sectionId() {
      return Number.parseInt(this.$route.params.sectionId);
    },

    postId() {
      return Number.parseInt(this.$route.params.postId);
    },

    post() {
      return Post.query()
        .where('id', this.postId)
        .with('learning_unit')
        .with('meeting.meeting_records.vimeo')
        .first();
    },

    postSchedule() {
      return PostSchedule.query()
        .where('post_id', this.postId)
        .where('section_id', this.sectionId)
        .first();
    },

    learningUnit() {
      return this.post?.learning_unit;
    },

    breadcrumbs() {
      const { learningUnitId } = this;

      return [
        {
          key: 'design-and-planning',
          name: 'Design and planning',
          to: {
            name: 'design-planning'
          }
        },
        {
          key: 'learning-unit-info',
          name: this.learningUnit?.name || 'Untitled',
          to: {
            name: 'design-planning/info',
            params: {
              learningUnitId
            }
          }
        },
        {
          key: 'manage-post',
          name: 'Edit Video Conference',
          active: true
        }
      ];
    },

    titleErrorMessage() {
      const titleValidate = this.$v.form.title;
      if (!titleValidate.required) {
        return 'The Title is a required field.';
      } else if (!titleValidate.minLength) {
        return 'The Title must be at least 3 characters.';
      } else if (!titleValidate.maxLength) {
        return 'The Title not be greater than 255 characters.';
      } else return '';
    }
  },

  async created() {
    await this.init();
  },

  methods: {
    /**
     * fetch post video conference data
     */
    async init() {
      try {
        this.isLoading = true;

        await this.fetch();
        this.initialData();

        this.isLoading = false;
      } catch (error) {
        this.isError = true;
        console.error(error);
      }
    },

    async fetch() {
      await PostSchedule.api().findAll({
        post: this.postId,
        section: this.sectionId
      });
      await Post.api().fetchById(this.postId);
    },

    async initialData() {
      const { title, description, is_published } = this.post;
      this.form = {
        title: title,
        description: description,
        is_published: is_published,
        published_at: this.postSchedule.published_at
          ? this.postSchedule.published_at
          : new Date().toString()
      };
      this.records = this.post?.meeting?.meeting_records;
    },

    async submit() {
      this.$v.form.$touch();

      /* validate */
      if (this.$v.form.title.$invalid) {
        this.$refs['input-title'].scrollIntoView({ behavior: 'smooth' });
        return;
      }

      this.isSubmitting = true;

      try {
        if (!this.form.is_published) this.form.published_at = null;

        /* save post */
        const resultPost = Post.api().updatePost(this.postId, this.form);

        /* save post schedule */
        const resultPostSchedule = PostSchedule.api().update(this.postSchedule.id, {
          published_at: this.form.published_at
        });

        /* update meeting record list */
        const resultRecords = this.records.map((record) => {
          return MeetingRecord.api().update(record.id, {
            is_listed: record.is_listed
          });
        });

        await Promise.all([resultPost, resultPostSchedule, ...resultRecords]);

        this.notiSuccess('post', 'updated');
      } catch (error) {
        console.error(error);
        this.notiError();
      }

      this.isSubmitting = false;
      this.goBackToPreviousPage();
    },

    formatRecordDuration(seconds) {
      return moment.utc(seconds * 1000).format('HH:mm:ss');
    },

    showModalVideo(index) {
      this.$bvModal.show(`modal__view_video-${index}`);
    },

    goBackToPreviousPage() {
      if (this.$route.query.previousPath) {
        const { previousPath } = this.$route.query;

        this.$router.push({ path: previousPath });
      } else {
        this.$router.push({
          name: 'design-planning/teaching-feed',
          params: {
            learningUnitId: this.learningUnitId,
            sectionId: this.sectionId
          },
          query: {
            view: 'list'
          }
        });
      }
    }
  }
};
</script>

<style scoped>
.display-cover {
  width: 320px;
  cursor: pointer;
}
</style>

<style>
.ql-editor.ql-blank::before {
  font-family: Heebo;
  font-style: normal;
  font-weight: normal;
  font-size: 15.2px;
  line-height: 22px;
  color: #d1d2db;
}

.ql-editor {
  height: 10rem;
}

.ql-container.ql-snow {
  background: #ffffff;
  border: 1px solid #d1d2db;
  box-sizing: border-box;
  border-radius: 0 0 4.64px 4.64px;
}

.ql-toolbar.ql-snow {
  background: #ffffff;
  border: 1px solid #d1d2db;
  box-sizing: border-box;
  border-radius: 4.64px 4.64px 0 0;
}
</style>
